import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { buttonClasses, filterClasses } from "../common/tailwindClassnames";
import SelectInput from "../common/SelectInput";
import { routeDisplayName } from "../routes/routeDisplayName";

// A route RECORD from /v2/routes/: its own name and destinations, with the
// provider as an integration summary under data_providers. `provider` is kept for
// the older connection-shaped callers.
type RouteCandidate = {
    id: string;
    name?: string;
    provider?: { name?: string; type?: { name?: string } };
    data_providers?: { id?: string; name?: string; owner?: { name?: string }; type?: { name?: string } }[];
    owner?: { id: string; name: string } | string;
    destinations?: { id: string }[];
};
type FilterOption = { label: string; value: string };

// A record's `owner` can come back as an id string; only an object carries a name.
const ownerName = (c: RouteCandidate) =>
    typeof c.owner === "object" && c.owner ? c.owner.name : undefined;

type AddToRouteDrawerProps = {
    isOpen: boolean;
    onClose: () => void;
    candidates: RouteCandidate[];
    loading: boolean;
    // Attach this record to the chosen Route; the parent owns the PATCH.
    onAttach: (route: RouteCandidate) => Promise<void> | void;
    onSearch: (searchStr: string) => void;
    hasMore: boolean;
    workspaceOptions: FilterOption[];
    workspaceFilter: FilterOption | null;
    onChangeWorkspaceFilter: (option: FilterOption | null) => void;
    loadingWorkspaces?: boolean;
};

// Mirror of the flow map's AddDestinationDrawer, pointed the other way: instead of
// picking destinations for a Route, pick a Route for this record. Radio
// selection — a record joins one Route per confirm.
const AddToRouteDrawer = ({
    isOpen, onClose, candidates, loading, onAttach, onSearch, hasMore,
    workspaceOptions, workspaceFilter, onChangeWorkspaceFilter, loadingWorkspaces,
}: AddToRouteDrawerProps) => {
    const { t } = useTranslation();
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const [query, setQuery] = useState("");
    // Only queries the user actually typed (debounced) go back up as a reload.
    const lastSent = useRef<string | null>(null);

    useEffect(() => {
        if (!isOpen) { setSelectedId(null); setSaving(false); setQuery(""); lastSent.current = null; }
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        if (lastSent.current === null) { lastSent.current = query; return; }
        if (query === lastSent.current) return;
        const timer = setTimeout(() => { lastSent.current = query; onSearch(query); }, 300);
        return () => clearTimeout(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query, isOpen]);

    const selected = candidates.find((c) => c.id === selectedId);

    return (
        <div
            data-testid="AddToRouteDrawer-panel"
            className={`fixed top-0 right-0 h-full w-full md:w-[420px] bg-white border-l border-gray-lines shadow-xl z-[60] transition-transform duration-300 flex flex-col ${
                isOpen ? "translate-x-0" : "translate-x-full pointer-events-none"}`}
            aria-hidden={!isOpen}
            {...({ inert: isOpen ? undefined : "" } as any)}>
            <div className="flex items-center justify-between px-4 py-4 border-b border-gray-lines">
                <h2 className="text-lg font-semibold text-off-black">{t("Add to a Route")}</h2>
                <button onClick={onClose} aria-label={t("Close")}
                    className="w-9 h-9 flex items-center justify-center text-medium-gray hover:text-off-black">✕</button>
            </div>

            <div className="px-4 pt-4 flex flex-col gap-3">
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t("Search Routes")}
                    className="w-full border border-field-outline rounded px-3 py-2 text-sm text-off-black placeholder:text-secondary-text focus:outline-none focus:border-route-green"
                />
                <div className="flex flex-col gap-1">
                    <span className={filterClasses.label}>{t("Workspace")}</span>
                    <SelectInput
                        name="drawer_route_workspace"
                        options={workspaceOptions}
                        value={workspaceFilter}
                        onChange={onChangeWorkspaceFilter}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={!!loadingWorkspaces}
                        className=""
                        variant="routeFilter"
                    />
                </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
                {loading
                    ? [0, 1, 2, 3].map((i) => <div key={i} className="h-10 w-full bg-neutral-gray-50 rounded animate-pulse mb-2" />)
                    : candidates.length === 0
                        ? <p className="text-sm text-secondary-text mb-4">
                            {query ? t("No Routes match your search.")
                                : workspaceFilter ? t("No eligible Routes in this workspace.")
                                    : t("No eligible Routes.")}
                          </p>
                        : candidates.map((c) => (
                            <label key={c.id} className="flex items-start gap-3 py-2 border-b border-gray-lines cursor-pointer">
                                <input type="radio" name="target-route" className="mt-1 accent-route-green"
                                    checked={selectedId === c.id}
                                    onChange={() => setSelectedId(c.id)} />
                                <span className="min-w-0 flex-1">
                                    {/* The Route's own name — the picker lists route
                                        records, and a renamed Route must read the same
                                        here as everywhere else. */}
                                    <span className="block text-sm text-off-black truncate">{routeDisplayName(c) || c.name || c.id}</span>
                                    {(ownerName(c) || c.data_providers?.[0]?.owner?.name) && (
                                        <span className="block text-xs text-secondary-text truncate">
                                            {ownerName(c) || c.data_providers?.[0]?.owner?.name}
                                        </span>
                                    )}
                                </span>
                                {(c.provider?.type?.name || c.data_providers?.[0]?.type?.name) && (
                                    <span className="flex-none bg-light-green text-route-green rounded-full px-2 py-0.5 text-[11px] font-semibold">
                                        {c.provider?.type?.name || c.data_providers?.[0]?.type?.name}
                                    </span>
                                )}
                            </label>
                        ))}
                {!loading && hasMore && (
                    <p className="text-xs text-secondary-text mt-2">
                        {t("Showing the first matches only — refine your search to find more.")}
                    </p>
                )}
            </div>

            <div className="flex items-center gap-3 px-4 py-3 border-t border-gray-lines">
                <button className={buttonClasses.primary} disabled={!selected || saving}
                    onClick={async () => {
                        if (!selected) return;
                        setSaving(true);
                        try { await onAttach(selected); } finally { setSaving(false); }
                    }}>
                    {t("Add to Route")}
                </button>
                <button className={buttonClasses.cancel} onClick={onClose}>{t("Cancel")}</button>
            </div>
        </div>
    );
};

export default AddToRouteDrawer;
